/**
 * Pure, framework-free helpers for replaying queued offline actions.
 *
 * What was broken: the field app queued job photos as base64 JSON to
 * `/api/attachments/upload`, but that endpoint is multipart-only and
 * returned `400 FILE_REQUIRED`. The offline queue treated ANY 4xx as a
 * permanent failure and deleted the entry — so a tech who shot photos
 * offline lost them silently on reconnect. Failure mode: permanent,
 * invisible data loss of field photos.
 *
 * This module lives apart from `offline-sync.ts` (which imports React) so
 * the service worker (`sw.ts`) can share the exact same replay + retry
 * semantics without pulling React into the SW bundle.
 */

export interface OfflineFileUpload {
    /** multipart field name for the binary (matches multer `.single("file")`). */
    fileField: string;
    /** base64 data URL of the file payload. */
    dataUrl: string;
    /** filename to send in the multipart part. */
    fileName: string;
    /** extra text fields (jobId, category, photoTag, ...); null values are skipped. */
    fields: Record<string, string | null>;
}

export interface OfflineActionLike {
    url: string;
    method: string;
    body: unknown;
    /** When present, the action replays as multipart/form-data, not JSON. */
    fileUpload?: OfflineFileUpload;
}

/** Max replay attempts before a stuck entry is parked as failed (visible,
 *  retryable — never silently dropped). */
export const MAX_REPLAY_ATTEMPTS = 8;

/**
 * A 4xx (other than 408/429) is a semantic server rejection — replaying the
 * identical payload cannot succeed. Matches `isPermanentSaveRejection` in
 * save-outbox.ts so the write-through path and the drains agree.
 *
 * History: this was briefly "only 422" because the original drain DELETED
 * permanent failures, and a client bug (photos queued as JSON against the
 * multipart-only upload endpoint) was 400ing — treating 400 as permanent
 * silently destroyed field photos. The drains now PARK permanent failures
 * in the queue with the server's error message (offline-sync.ts
 * `markActionFailed`) instead of deleting them, so classifying real 4xx
 * rejections as permanent is safe again: the tech sees the failure
 * immediately ("N saves didn't sync") rather than after 8 futile retries.
 */
export function isPermanentFailure(status: number): boolean {
    return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/**
 * A replay whose response actually means "this mutation already landed on
 * the server" — an idempotent no-op, NOT a failure.
 *
 * The point-recording endpoints (complete-test, record-repair, …) return
 * `409 { error: "ALREADY_RECORDED" }` when the tech's line for
 * (job, device, task) is already on the books. That happens whenever a
 * queued write replays a request that had, in fact, succeeded the first
 * time — the classic timed-out-but-landed case, or a re-tap. The ONLINE
 * path already treats this exact 409 as silent success (see
 * `fireBackflowCompleteTest`), because the tech's point is recorded either
 * way. A queued replay must reach the same conclusion: the outbox's only
 * job — get the write onto the server — is done, so the entry should be
 * CLEARED as synced, never parked.
 *
 * Without this carve-out a test that saved perfectly (its complete-test
 * point already recorded) leaves an undismissable red "save didn't sync"
 * whose Retry can only ever re-hit the same 409 — the "retry just glitches"
 * report. This is deliberately narrow: only a 409 whose JSON body carries
 * `error: "ALREADY_RECORDED"` qualifies, so a name-collision 409 (error
 * "CONFLICT") or any other rejection still parks honestly.
 */
export function isAlreadyLanded(status: number, bodyText: string): boolean {
    if (status !== 409) return false;
    try {
        const parsed = JSON.parse(bodyText) as { error?: unknown };
        return parsed?.error === "ALREADY_RECORDED";
    } catch {
        return false;
    }
}

/**
 * Pull a plain-English reason out of a replay response body, for the tech.
 *
 * Martin High School (10/27): a queued test replayed into a 409
 * ASSET_SITE_MISMATCH whose body said exactly what to do ("This device
 * belongs to …, not this job's site. Open that site's job to save its
 * test."), and the iPad showed "Save failed (409)". The server's `message`
 * is written for staff, so it always wins. Without one we fall back to a
 * plain sentence picked by the kind of rejection — never the error code,
 * the HTTP status, or the raw body (which can be HTML or a stack trace).
 */
export function extractReplayErrorMessage(bodyText: string, status: number): string {
    try {
        const parsed = JSON.parse(bodyText) as { message?: unknown };
        if (typeof parsed.message === "string" && parsed.message.trim()) return parsed.message.trim();
    } catch {
        /* not JSON */
    }
    return plainRejectionMessage(status);
}

/** A plain sentence for a server rejection that came with no message. */
export function plainRejectionMessage(status: number | null): string {
    if (status === 401) return "You were signed out, so this did not send. Sign in again, then tap Retry.";
    if (status === 403) return "Your account is not allowed to make this change. Call the office.";
    if (status === 404) return "The job or device for this save is no longer in the system. Call the office.";
    if (status === 413) return "This file is too big to send. Take a smaller photo, or call the office.";
    if (status !== null && status >= 500) return "The office system had a problem taking this save.";
    if (status === null) return "This did not send because there was no connection.";
    return "The office system would not take this save. Call the office.";
}

/** The parked note for a save that kept failing on signal or server trouble. */
export function retryExhaustedMessage(attempts: number): string {
    return `Tried ${attempts} times and it still did not send.`;
}

/**
 * What the tech should do next with a parked save. Shown under the reason in
 * "Saves that didn't sync" so every row ends with a step, and so it is clear
 * the readings are still on the device.
 */
export function failureNextStep(failure: { terminal: boolean }): string {
    return failure.terminal
        ? "Your entries are still saved on this device. Do what the note above says. Once it is fixed, tap Retry. Not sure? Call the office before you discard anything."
        : "Your entries are still saved on this device. Tap Retry when you have good signal.";
}

/** Decode a base64 (or URL-encoded) data URL into a Blob. */
export function dataUrlToBlob(dataUrl: string): Blob {
    const comma = dataUrl.indexOf(",");
    const header = comma >= 0 ? dataUrl.slice(0, comma) : "";
    const data = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
    const mimeMatch = header.match(/^data:([^;]+)/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const isBase64 = /;base64/i.test(header);
    const binary = isBase64 ? atob(data) : decodeURIComponent(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

export interface ReplayRequest {
    body: BodyInit | undefined;
    headers: Record<string, string>;
}

/**
 * Build the fetch body + headers for a queued action. File uploads become
 * multipart FormData (no Content-Type header, so the platform sets the
 * boundary); everything else stays JSON. The caller adds Authorization.
 */
export function buildReplayRequest(action: OfflineActionLike): ReplayRequest {
    if (action.fileUpload) {
        const fd = new FormData();
        for (const [k, v] of Object.entries(action.fileUpload.fields)) {
            if (v !== null && v !== undefined) fd.append(k, v);
        }
        const blob = dataUrlToBlob(action.fileUpload.dataUrl);
        fd.append(action.fileUpload.fileField, blob, action.fileUpload.fileName);
        return { body: fd, headers: {} };
    }
    return {
        body: action.body ? JSON.stringify(action.body) : undefined,
        headers: { "Content-Type": "application/json" },
    };
}
